
import { useState } from 'react';
import { DrinkOption } from '../../types';
import Card from '../base/Card';
import Button from '../base/Button';

interface DrinkCardProps {
  drink: DrinkOption;
  onAddToCart: (
    drink: DrinkOption,
    milk: 'Whole Milk' | 'Oat Milk',
    temperature: 'Hot' | 'Iced',
    quantity: number,
    flavor?: 'Rose' | 'Lavender'
  ) => void;
}

export default function DrinkCard({ drink, onAddToCart }: DrinkCardProps) {
  const [milk, setMilk] = useState<'Whole Milk' | 'Oat Milk'>('Whole Milk');
  const [temperature, setTemperature] = useState<'Hot' | 'Iced'>('Hot');
  const [flavor, setFlavor] = useState<'Rose' | 'Lavender'>('Rose');
  const [quantity, setQuantity] = useState(1);

  const handleAddToCart = () => {
    onAddToCart(
      drink,
      milk,
      temperature,
      quantity,
      drink.hasFlavorChoice ? flavor : undefined
    );
    
    // Reset to defaults
    setQuantity(1);
  };

  return (
    <Card hover className="h-full flex flex-col">
      <div className="flex-1">
        <h3 className="text-xl font-script text-navy mb-2">{drink.name}</h3>
        <p className="text-navy/70 text-sm mb-4 leading-relaxed">{drink.description}</p>
        <div className="text-lg font-medium text-navy mb-6">$0.00</div>
      </div>

      <div className="space-y-4">
        {/* Milk Selection */}
        <div>
          <label className="block text-sm font-medium text-navy mb-2">
            Milk <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-2">
            {['Whole Milk', 'Oat Milk'].map((option) => (
              <button
                key={option}
                onClick={() => setMilk(option as 'Whole Milk' | 'Oat Milk')}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors whitespace-nowrap cursor-pointer ${
                  milk === option
                    ? 'bg-blue-700 text-cream border-blue-700'
                    : 'bg-white text-navy border-gray-300 hover:border-blue-700'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        {/* Temperature Selection */}
        <div>
          <label className="block text-sm font-medium text-navy mb-2">
            Temperature <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-2">
            {['Hot', 'Iced'].map((option) => (
              <button
                key={option}
                onClick={() => setTemperature(option as 'Hot' | 'Iced')}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors whitespace-nowrap cursor-pointer ${
                  temperature === option
                    ? 'bg-blue-700 text-cream border-blue-700'
                    : 'bg-white text-navy border-gray-300 hover:border-blue-700'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        {/* Flavor Selection for London Fog */}
        {drink.hasFlavorChoice && (
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Flavor
            </label>
            <div className="grid grid-cols-2 gap-2">
              {['Rose', 'Lavender'].map((option) => (
                <button
                  key={option}
                  onClick={() => setFlavor(option as 'Rose' | 'Lavender')}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors whitespace-nowrap cursor-pointer ${
                    flavor === option
                      ? 'bg-blue-700 text-cream border-blue-700'
                      : 'bg-white text-navy border-gray-300 hover:border-blue-700'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quantity */}
        <div>
          <label className="block text-sm font-medium text-navy mb-2">
            Quantity
          </label>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-300 hover:border-blue-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-subtract-line text-sm"></i>
            </button>
            <span className="text-lg font-medium text-navy min-w-[2rem] text-center">
              {quantity}
            </span>
            <button
              onClick={() => setQuantity(quantity + 1)}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-300 hover:border-blue-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line text-sm"></i>
            </button>
          </div>
        </div>

        <Button onClick={handleAddToCart} className="w-full">
          Add to Cart
        </Button>
      </div>
    </Card>
  );
}
